import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';
import { logAction } from '../lib/actionLogger';

function mapFromDb(row) { return snakeToCamel(row); }
function mapToDb(obj) { return camelToSnake(obj); }

export function useTimeEntries() {
  return useQuery({
    queryKey: queryKeys.timeEntries.all,
    queryFn: async () => {
      const data = await api.get('/time-entries');
      return data.map(mapFromDb);
    },
  });
}

export function useTimeEntriesByJob(jobId) {
  return useQuery({
    queryKey: queryKeys.timeEntries.byJob(jobId),
    queryFn: async () => {
      const data = await api.get(`/time-entries?job_id=${jobId}`);
      return data.map(mapFromDb);
    },
    enabled: !!jobId,
  });
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newEntry) => {
      const data = await api.post('/time-entries', mapToDb(newEntry));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      if (data.jobId) {
        qc.invalidateQueries({ queryKey: queryKeys.timeEntries.byJob(data.jobId) });
      }
      logAction({
        action: 'Create Time Entry',
        jobId: data.jobId,
        details: { hours: data.hours, role: data.role, worker: data.worker },
      });
    },
  });
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entry) => {
      const { id, ...updates } = entry;
      const data = await api.put(`/time-entries/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      if (data.jobId) {
        qc.invalidateQueries({ queryKey: queryKeys.timeEntries.byJob(data.jobId) });
      }
      logAction({
        action: 'Update Time Entry',
        jobId: data.jobId,
        details: { hours: data.hours },
      });
    },
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId) => {
      await api.del(`/time-entries/${entryId}`);
    },
    onSuccess: (_data, entryId) => {
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      qc.invalidateQueries({ queryKey: ['time_entries', 'job'] });
      logAction({
        action: 'Delete Time Entry',
        details: { entryId },
      });
    },
  });
}
